import React, { useState } from "react"
import { OrderHistoryLogEntry, OrderSearchHit } from "lib/types/generated/graphql-types"
import {
  DateTime,
  ExpandButton, MissingRow,
  Reference,
  ReferenceText,
  Section,
  SmallInfo,
  TableRow
} from "./CustomerOrderTable.styled"
import dayjs from "dayjs"
import { ReactComponent as DownIcon } from "images/icons/chevron-down.svg"
import GET_ORDER from "../../graphql/queries/order/GetOrder"
import { useLazyQuery } from "@apollo/client"
import { getCountryName } from "helpers/countries"
import { CustomerOrders } from "../../classes/customer/CustomerOrders"
import Status from "./Status"
import CustomStateRow from "./CustomStateRow"
import CommentRow from "./CommentRow"
import DotLoader from "../Ui/DotLoader"

type Props = {
  searchOrder: OrderSearchHit
  customerOrders: CustomerOrders
  sideBarCollapsed: boolean
}

const CustomerOrderTableRow = ({ searchOrder, customerOrders, sideBarCollapsed }: Props) => {
  const [expanded, setExpanded] = useState(false)
  const [entries, setEntries] = useState<OrderHistoryLogEntry[]>()

  const appliedStates = searchOrder?.status?.customStates?.map((state) => state.id)

  const blockingOperations = customerOrders.getBlockingOperations(appliedStates ?? [])

  const [getOrder, { loading }] = useLazyQuery(GET_ORDER, {
    onCompleted: (data) => {
      setEntries(
        customerOrders.getFilteredOrderLogEntries(data.Order?.history?.history, appliedStates ?? [])
      )
      setExpanded(!expanded)
    }
  })

  const toggleExpand = () => {
    if (expanded) {
      setExpanded(false)
      return
    }
    if (entries && entries.length >= 0) {
      setExpanded(true)
      return
    }
    getOrder({ variables: { id: searchOrder.id } })
  }

  return (
    <>
      <TableRow className={loading ? "blur" : ""} $expanded={expanded} $sideBarCollapsed={sideBarCollapsed}>
        <Reference>
          <ExpandButton onClick={toggleExpand} $expanded={expanded}>
            <DownIcon />
          </ExpandButton>
          <div>
            <ReferenceText to={`/orders/${searchOrder.id}`}>
              #{searchOrder.reference}
            </ReferenceText>
            <SmallInfo className={"hideOnSmallScreens"}>OrderId: {searchOrder.id}</SmallInfo>
          </div>
        </Reference>
        <Section className={"hideOnMediumScreens"}>
          {customerOrders.getStoreGroupName(searchOrder.storeGroupId) ?? <DotLoader/>}
        </Section>
        <Section className={sideBarCollapsed ? "hideOnSmallScreens" : "hideOnMediumScreens"}>
          {getCountryName(searchOrder.countryCode)}
        </Section>
        <DateTime>
          <div>
            <span>
              <b>{dayjs(searchOrder.date).format("YYYY-MM-DD")}</b>
            </span>
            <span>{dayjs(searchOrder.date).format("HH:mm")}</span>
          </div>
        </DateTime>
        <Status blockingOperations={blockingOperations} orderId={searchOrder.id} />
      </TableRow>
      {expanded && (
        <div>
          {entries && entries.length > 0 ? (
            <>
              {entries?.map((entry) =>
                entry.type === "ADD_COMMENT" ? (
                  <CommentRow entry={entry} key={entry.operationId + entry.timestamp} />
                ) : (
                  <CustomStateRow entry={entry} key={entry.operationId + entry.timestamp} customerOrders={customerOrders} />
                )
              )}
            </>
          ) : (
            <MissingRow>
              <Reference> - No comments or custom states associated with this order.</Reference>
            </MissingRow>
          )}
        </div>
      )}
    </>
  )
}

export default CustomerOrderTableRow
